import { makeStyles } from "@saleor/macaw-ui";
import { Text } from "@saleor/macaw-ui-next";
import { FileText } from "lucide-react";
import React from "react";

const useStyles = makeStyles(
  theme => ({
    sectionContainer: {
      marginBottom: theme.spacing(3),
      backgroundColor: "#fff",
      border: "1px solid #e0e0e0",
      borderRadius: 8,
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      overflow: "hidden",
    },
    sectionHeader: {
      padding: theme.spacing(2, 3, 1.5, 0),
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      backgroundColor: "#fff",
    },
    tableHeader: {
      backgroundColor: "#f5f5f5",
      borderBottom: "1px solid #e0e0e0",
    },
    tableHeaderCell: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 600,
      color: "#616161",
      textTransform: "uppercase",
    },
    tableRow: {
      borderBottom: "1px solid #f0f0f0",
      "&:hover": {
        backgroundColor: "#fafafa",
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },
    tableCell: {
      padding: "12px 16px",
    },
    pageName: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    statusBadge: {
      padding: "4px 12px",
      borderRadius: 16,
      fontSize: "12px",
      fontWeight: 500,
      display: "inline-block",
    },
    publishedBadge: {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
    draftBadge: {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
  }),
  { name: "PageSection" },
);

interface PageNode {
  id: string;
  title: string;
  slug: string;
  created: string;
  isPublished: boolean;
}

interface PageSectionProps {
  title: string;
  description: string;
  pages: PageNode[];
  onPageClick?: (id: string) => void;
}

export const PageSection: React.FC<PageSectionProps> = ({
  title,
  description,
  pages,
  onPageClick,
}) => {
  const classes = useStyles();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  return (
    <>
      <div className={classes.sectionHeader}>
        <Text size={5} fontWeight="bold" display="block" marginBottom={1}>
          {title}
        </Text>
        <Text size={2} color="default2" display="block">
          {description}
        </Text>
      </div>
      <div className={classes.sectionContainer}>
        <table className={classes.table}>
          <thead className={classes.tableHeader}>
            <tr>
              <th className={classes.tableHeaderCell}>Page</th>
              <th className={classes.tableHeaderCell}>URL</th>
              <th className={classes.tableHeaderCell}>Status</th>
              <th className={classes.tableHeaderCell}>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr className={classes.tableRow}>
                <td className={classes.tableCell} colSpan={4} style={{ textAlign: "center" }}>
                  <Text color="default2">No pages found</Text>
                </td>
              </tr>
            ) : (
              pages.map(page => (
                <tr
                  key={page.id}
                  className={classes.tableRow}
                  style={{ cursor: "pointer" }}
                  onClick={() => onPageClick?.(page.id)}
                >
                  <td className={classes.tableCell}>
                    <div className={classes.pageName}>
                      <FileText size={16} color="#616161" />
                      <Text>{page.title}</Text>
                    </div>
                  </td>
                  <td className={classes.tableCell}>
                    <Text color="default2" size={2}>
                      /{page.slug}
                    </Text>
                  </td>
                  <td className={classes.tableCell}>
                    <span
                      className={`${classes.statusBadge} ${
                        page.isPublished ? classes.publishedBadge : classes.draftBadge
                      }`}
                    >
                      {page.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className={classes.tableCell}>
                    <Text color="default2" size={2}>
                      {formatDate(page.created)}
                    </Text>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

PageSection.displayName = "PageSection";
export default PageSection;
